import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Clock, ChevronRight, Filter, Users, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STAGE_LABELS = {
    // Courtage
    'ep_debut_prospection': 'Début prospection',
    'ep_facture_recue': 'Facture reçue',
    'ep_mandat_envoye': 'Mandat envoyé',
    'ep_mandat_signe': 'Mandat signé',
    'ep_dossier_transmis': 'Dossier transmis',
    'ep_etude_recue': 'Étude reçue',
    'ep_etude_expliquee': 'Étude expliquée',
    'ep_rib_receptionne': 'RIB réceptionné',
    'ep_contrat_envoye': 'Contrat envoyé',
    'ep_contrat_explique': 'Contrat expliqué',
    // Travaux d’optimisation
    'ch_formulaire_rempli': 'Formulaire rempli',
    'ch_visite_planifiee': 'Visite planifiée',
    'ch_visite_effectuee': 'Visite effectuée',
    'ch_dossier_etude': 'Dossier étude',
    'ch_etude_presentee': 'Étude présentée',
    'ch_devis_envoye': 'Devis envoyé',
    'ch_devis_accepte': 'Devis accepté',
    'ch_planification': 'Planification',
    'ch_en_cours': 'En cours',
    'ch_termine': 'Terminé',
    'ch_attente_paiement': 'Attente paiement',
};

export function OverdueProspectsWidget() {
    const navigate = useNavigate();
    const { user, isAdminFull, isAdmin, isVP } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ total: 0, prospects: [], by_stage: {} });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [stageFilter, setStageFilter] = useState('all');
    const [pipelineFilter, setPipelineFilter] = useState('all');

    const canSeeWidget = isAdminFull() || isAdmin() || isVP();

    useEffect(() => {
        if (canSeeWidget) {
            loadData();
        }
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const res = await dashboardAPI.getOverdueProspects({ days_threshold: 10 });
            setData(res.data || { total: 0, prospects: [], by_stage: {} });
        } catch (error) {
            console.error('Error loading overdue prospects:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!canSeeWidget) return null;

    const filteredProspects = data.prospects.filter(p => {
        // Module ciblé par le pipeline (les filtres d'étape ne portent que sur ce module)
        const scopeMod = pipelineFilter === 'easy_power' ? 'easy_power'
            : pipelineFilter === 'chantier' ? 'chantier' : null;
        if (pipelineFilter !== 'all') {
            if (pipelineFilter === 'easy_power' && !p.easy_power_stage) return false;
            if (pipelineFilter === 'chantier' && !p.chantier_stage) return false;
        }
        if (stageFilter !== 'all') {
            const epStage = p.easy_power_stage;
            const chStage = p.chantier_stage;
            if (scopeMod) {
                const modStage = scopeMod === 'chantier' ? chStage : epStage;
                if (modStage !== stageFilter) return false;
            } else if (epStage !== stageFilter && chStage !== stageFilter) {
                return false;
            }
        }
        return true;
    });

    // Étapes proposées, scopées au module sélectionné (ex. Travaux d’optimisation → uniquement étapes ch_)
    const stageOptions = Object.keys(data.by_stage || {}).filter(stage => {
        if (pipelineFilter === 'easy_power') return stage.startsWith('ep_');
        if (pipelineFilter === 'chantier') return stage.startsWith('ch_');
        return true;
    });

    return (
        <>
            <Card 
                className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-orange-500 hover:scale-[1.01]"
                onClick={() => setDialogOpen(true)}
                data-testid="overdue-prospects-widget"
            >
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Dossiers en retard</p>
                            <p className="text-3xl font-bold text-orange-600">{loading ? '...' : data.total}</p>
                            <p className="text-xs text-slate-400 mt-1">Plus de 2 semaines sans activité</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-orange-600" />
                        </div>
                    </div>
                    <p className="text-xs text-orange-600 mt-3 flex items-center">
                        <ChevronRight className="w-4 h-4" />
                        Cliquer pour voir la liste
                    </p>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-500" />
                            Dossiers en retard ({filteredProspects.length})
                        </DialogTitle>
                    </DialogHeader>
                    
                    {/* Filtres */}
                    <div className="flex gap-4 py-3 border-b">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-slate-400" />
                            <Select value={pipelineFilter} onValueChange={(v) => { setPipelineFilter(v); setStageFilter('all'); }}>
                                <SelectTrigger className="w-[150px]" data-testid="pipeline-filter">
                                    <SelectValue placeholder="Pipeline" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous pipelines</SelectItem>
                                    <SelectItem value="easy_power">Courtage</SelectItem>
                                    <SelectItem value="chantier">Travaux d’optimisation</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Select value={stageFilter} onValueChange={setStageFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="stage-filter">
                                    <SelectValue placeholder="Étape" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes étapes</SelectItem>
                                    {stageOptions.map(stage => (
                                        <SelectItem key={stage} value={stage}>
                                            {STAGE_LABELS[stage] || stage} ({data.by_stage[stage]})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Liste */}
                    <div className="flex-1 overflow-auto">
                        {filteredProspects.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                                <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                <p>Aucun dossier en retard</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Dossier</TableHead>
                                        <TableHead>Responsable</TableHead>
                                        <TableHead>Étape</TableHead>
                                        <TableHead>Retard</TableHead>
                                        <TableHead>Dernière activité</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredProspects.map((prospect) => (
                                        <TableRow 
                                            key={prospect.id}
                                            className="cursor-pointer hover:bg-slate-50"
                                            onClick={() => {
                                                setDialogOpen(false);
                                                navigate(`/prospects/${prospect.id}`);
                                            }}
                                            data-testid={`overdue-row-${prospect.id}`}
                                        >
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-slate-900">
                                                        {prospect.company_name || `${prospect.first_name || ''} ${prospect.last_name || ''}`}
                                                    </p>
                                                    {prospect.company_name && (
                                                        <p className="text-xs text-slate-500">
                                                            {prospect.first_name} {prospect.last_name}
                                                        </p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-sm">
                                                    <Users className="w-3.5 h-3.5 text-slate-400" />
                                                    {prospect.dossier_apporteur_name || prospect.dossier_vp_name || '-'}
                                                </div>
                                                {prospect.team_name && (
                                                    <p className="text-xs text-slate-400">{prospect.team_name}</p>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-1">
                                                    {prospect.easy_power_stage && (
                                                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                                                            EP: {STAGE_LABELS[prospect.easy_power_stage] || prospect.easy_power_stage}
                                                        </Badge>
                                                    )}
                                                    {prospect.chantier_stage && (
                                                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                                            CH: {STAGE_LABELS[prospect.chantier_stage] || prospect.chantier_stage}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge 
                                                    className={`${
                                                        prospect.days_overdue > 30 
                                                            ? 'bg-red-100 text-red-700' 
                                                            : prospect.days_overdue > 21 
                                                                ? 'bg-orange-100 text-orange-700'
                                                                : 'bg-yellow-100 text-yellow-700'
                                                    }`}
                                                >
                                                    {prospect.days_overdue} jours
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-500">
                                                {prospect.updated_at 
                                                    ? format(new Date(prospect.updated_at), 'dd/MM/yyyy', { locale: fr })
                                                    : '-'
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default OverdueProspectsWidget;
